// DsButton — implementa as classes de botão do canvas (DESIGN-SYSTEM.md §6.1):
//   `.btnp`   em 66 artboards -> tone="primary" size="compact" (e o neutro com override inline)
//   `.btnpri` em 21 artboards -> tone="primary" size="tall"
//   `.btnsec` em 18 artboards -> tone="neutral" size="tall"
//   `.btnout` em  7 artboards -> tone="success"
//
// A união de props é FECHADA: `variant`, `color`, `size`, `startIcon` e `endIcon` do MUI
// não passam adiante. Todo botão do canvas é rotulado — o "Atualizar" aparece sempre como
// `tone="neutral"` com texto, nunca como botão-ícone (não há botão-ícone redondo no canvas).
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.5
// Pedido por 7 LOTES (L07, L08, L09, L14, L18, L20, L24).
//
// `tone` tinha só `primary | neutral | success`. Os três tons que faltavam existem em
// artboard — não são invenção de lote. Cada um foi conferido no CSS do canvas antes de
// entrar aqui, e a união continua FECHADA (§1.2):
//
//   `error`    — gêmeo do `.btnout` em erro. Literal do canvas
//                (divida-pgfn-detalhe-por-declaracao, processo-detalhe-solicitacao-doc):
//                `font-size:12px;font-weight:700;color:var(--error-main);
//                 border:1px solid rgba(255,86,48,0.5);border-radius:8px;padding:7px 14px`.
//                Note que o erro usa `error.main`, enquanto o sucesso usa `success.dark` —
//                é assim nos dois artboards. Contornado antes com `sx` só de cor (L08, L14)
//                ou com `DsIconButton bare` (L24). Fica registrado, contra a dúvida de L24:
//                o canvas PREVÊ, sim, botão destrutivo rotulado.
//   `plain`    — o "Fechar" do rodapé de modal. Literal do canvas
//                (dte-dashboard-modal-rascunho, divida-consulta-modal-detalhe-debito):
//                `font-size:13px;font-weight:700;color:var(--text-primary);padding:8px 18px`,
//                sem borda e sem fundo. L07 e L14 implementaram local; L18 adotou `neutral`
//                com borda por falta de opção.
//   `inverted` — o "Nova Mensagem" do DTE. Literal do canvas: claro
//                `background:var(--grey-800);color:#FFFFFF`; escuro (dte-mensagens-Dark)
//                `background:var(--grey-700);color:#FFFFFF`. O `color="inherit"
//                variant="contained"` do MUI pinta BRANCO no escuro — o oposto (L09).
//
// Divergência medida e NÃO unificada: o submit do login (`login.dc.html`) NÃO é este
// `inverted`. Lá o `.btn` é `grey-800`/`common-white` no claro e INVERTE de verdade no
// escuro — `[data-color-scheme="dark"] .btn{background:var(--common-white);color:var(--grey-800)}`.
// Ou seja, para o login o comportamento padrão do MUI (`color="inherit" contained`) está
// certo e `tone="inverted"` estaria errado. L20 precisa saber disso; ver relatório.
//
// Pedido de L18 REABERTO E ATENDIDO nesta rodada: `loading` (§3.5).
// A recusa anterior ("o `Button` do MUI v6 não tem `loading`; seria trocar a base por
// `LoadingButton` do `@mui/lab`") partia de uma premissa FALSA sobre a versão. O projeto
// está em `@mui/material` **7.0.1** (`package.json`: `"^7.0.1"`;
// `node_modules/@mui/material/package.json`: `"version": "7.0.1"`), e o `Button` do v7
// declara `loading`, `loadingIndicator` e `loadingPosition`
// (`node_modules/@mui/material/Button/Button.d.ts:61,68,73`). O `LoadingButton` do `@mui/lab`
// é que foi DEPRECADO em favor disso; a base continua sendo o `Button` e o componente-raiz
// dos 66 artboards de `.btnp` não muda.
//
// Como o v7 se comporta, conferido em `Button/Button.js`:
//   - `disabled: disabled || loading` (`:568`) — o botão já sai inerte durante a carga;
//   - `loadingPosition` default `'center'` (`:504`), e o centro pinta
//     `&.MuiButton-loading{color:transparent}` (`:277-288`), especificidade (0,2,0), acima
//     do `color` do nosso `sx` (0,1,0): o rótulo some sozinho e o indicador fica no lugar
//     dele. O `DsIcon` usa `currentColor`, então some junto — é o que se quer;
//   - o indicador padrão é `<CircularProgress color="inherit" size={16} />` (`:512`), que
//     herda a cor do tom; nenhum tom do kit precisa de indicador próprio.
// As três props chegam ao `Button` pelo `...other`, que já as tipava por
// `Omit<ButtonProps, …>` — `loading` nunca esteve na lista de props removidas (§1.2 fecha
// `variant`/`color`/`size`/`startIcon`/`endIcon`, não o estado de carga). Ficam declaradas
// abaixo só para ficarem visíveis na API do kit e documentadas.
//
// Nenhum artboard desenha botão em carregamento: o indicador é o padrão do MUI, não um
// desenho do canvas — registrado como inferência, como manda §10. O que L18 já fez
// (`disabled` + rótulo no gerúndio) continua válido e não precisa ser reescrito.
//
// Único ajuste de casco: com `loadingPosition="start"`/`"end"` o MUI ocupa o slot de ícone
// com o indicador, e aí o `icon` do kit apareceria junto, duas marcas para o mesmo estado.
// Nesses dois casos o `icon` é suprimido enquanto `loading` for verdadeiro.
//
// Divergência registrada e descartada: em `relatorios` a mesma `.btnout` usa `primary.dark`
// com borda `rgba(0,167,111,0.5)` e `padding:6px 14px`. Vale a forma `success`, majoritária
// em 6 dos 7 artboards.

import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import type { ButtonProps } from '@mui/material/Button';

import { varAlpha } from 'minimal-shared/utils';

import Button from '@mui/material/Button';

import { DsIcon } from './DsIcon';
import { DS_BOX_RADIUS } from './tokens';

import type { DsIconName, DsIconSize } from './DsIcon';

// ----------------------------------------------------------------------

/** União FECHADA de tons. Nenhum deles é `color` do MUI repassado (§1.2). */
export type DsButtonTone = 'primary' | 'neutral' | 'success' | 'error' | 'plain' | 'inverted';

export type DsButtonProps = {
  /** Forma do botão no canvas. Padrão: 'primary'. */
  tone?: DsButtonTone;
  /**
   * Estado de carga do `Button` do MUI v7: mostra o indicador e desabilita o botão.
   * Não é desenho do canvas — nenhum artboard mostra botão carregando.
   */
  loading?: boolean;
  /** Indicador próprio. Padrão do MUI: `<CircularProgress color="inherit" size={16} />`. */
  loadingIndicator?: React.ReactNode;
  /** Onde o indicador entra. Padrão: 'center', que esconde o rótulo. */
  loadingPosition?: 'start' | 'end' | 'center';
  /**
   * Altura. Padrão: 'compact'. Os tons `success`, `error` e `plain` têm uma geometria
   * só, medida no artboard, e ignoram este valor.
   */
  size?: 'compact' | 'tall';
  /** Ícone de traço à esquerda do rótulo. */
  icon?: DsIconName;
  /** Tamanho do ícone. Padrão: 16 — o tamanho inline do `.btnp`. */
  iconSize?: DsIconSize;
  children: React.ReactNode;
} & Omit<ButtonProps, 'variant' | 'color' | 'size' | 'startIcon' | 'endIcon'>;

// ----------------------------------------------------------------------

/**
 * Tipografia e geometria comuns às formas. O `MuiButton` do tema parte de
 * `typography.button` (14px/700) com `padding:6px 16px` no medium; aqui travamos
 * 13px/700 e `boxShadow:'none'` — nenhum botão do canvas tem sombra.
 */
const SX_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  borderRadius: `${DS_BOX_RADIUS}px`,
  boxShadow: 'none',
} satisfies SystemStyleObject<Theme>;

/**
 * `.btnp` / `.btnpri`: fundo sólido, `border:0`, sem sombra em nenhum estado.
 *
 * O botão vai com `color="primary"`, e não `color="inherit"`: o ramo `inheritColor`
 * do tema (`theme/core/components/button.tsx:92-107`) pinta o `contained` de BRANCO
 * no dark via `theme.applyStyles('dark', …)`, e essa regra sai com o seletor
 * `*:where([data-color-scheme="dark"]) &` — especificidade (0,1,0), igual à do `sx`,
 * mas emitida depois dele pelo stylis. Com `color="inherit"` o `bgcolor` abaixo
 * perderia o empate e os 66 artboards de `.btnp` ficariam brancos no dark.
 */
const SX_PRIMARY = {
  border: 0,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
  '&:active': { boxShadow: 'none' },
} satisfies SystemStyleObject<Theme>;

/** `.btnsec` e o `.btnp` reaproveitado como neutro: transparente com borda de 1px. */
const SX_NEUTRAL = {
  bgcolor: 'transparent',
  color: 'text.primary',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} satisfies SystemStyleObject<Theme>;

/**
 * `.btnout`: 12px/700, `padding:7px 14px` e borda de `success.main` a 50% de alfa —
 * exatamente o `rgba(34,197,94,0.5)` do canvas, escrito por token.
 */
const sxSuccess = (theme: Theme) =>
  ({
    fontSize: 12,
    padding: '7px 14px',
    bgcolor: 'transparent',
    color: 'success.dark',
    border: '1px solid',
    borderColor: varAlpha(theme.vars.palette.success.mainChannel, 0.5),
    boxShadow: 'none',
    '&:hover': { bgcolor: 'transparent', boxShadow: 'none' },
  }) satisfies SystemStyleObject<Theme>;

/** No dark o `.btnout` troca o texto para `success.light`. */
const sxSuccessDark = (theme: Theme) =>
  theme.applyStyles('dark', { color: theme.vars.palette.success.light });

/**
 * Gêmeo do `.btnout` em erro: mesma geometria, `error.main` no texto e borda de
 * `error.main` a 50% de alfa — o `rgba(255,86,48,0.5)` literal do canvas.
 * Diferente do sucesso, o artboard NÃO troca a cor no dark: `error.main` já tem
 * contraste nos dois esquemas.
 */
const sxError = (theme: Theme) =>
  ({
    fontSize: 12,
    padding: '7px 14px',
    bgcolor: 'transparent',
    color: 'error.main',
    border: '1px solid',
    borderColor: varAlpha(theme.vars.palette.error.mainChannel, 0.5),
    boxShadow: 'none',
    '&:hover': { bgcolor: 'transparent', boxShadow: 'none' },
  }) satisfies SystemStyleObject<Theme>;

/**
 * O "Fechar" do rodapé de modal: texto puro, sem borda e sem fundo.
 * O `hover` em `action.hover` é inferência — o canvas não desenha estado para ele,
 * e um botão de rodapé de modal sem nenhuma resposta ao ponteiro lê como texto.
 */
const SX_PLAIN = {
  fontSize: 13,
  padding: '8px 18px',
  bgcolor: 'transparent',
  color: 'text.primary',
  border: 0,
  boxShadow: 'none',
  '&:hover': { bgcolor: 'action.hover', boxShadow: 'none' },
} satisfies SystemStyleObject<Theme>;

/**
 * "Nova Mensagem" do DTE: cinza sólido nos DOIS esquemas, texto sempre branco.
 * `grey.800` no claro e `grey.700` no escuro são literais dos artboards
 * `dte-mensagens` / `dte-mensagens-Dark`.
 *
 * O hover do claro é `grey.700`, literal de `login.dc.html` (`.btn:hover`). O do
 * escuro é `grey.600` — inferência, o canvas não desenha estado de hover no escuro.
 */
const sxInverted = (theme: Theme): SystemStyleObject<Theme> => ({
  border: 0,
  bgcolor: 'grey.800',
  color: 'common.white',
  boxShadow: 'none',
  '&:hover': { bgcolor: 'grey.700', boxShadow: 'none' },
  '&:active': { boxShadow: 'none' },
  ...theme.applyStyles('dark', {
    backgroundColor: theme.vars.palette.grey['700'],
    color: theme.vars.palette.common.white,
    '&:hover': { backgroundColor: theme.vars.palette.grey['600'] },
  }),
});

/** Geometria por tamanho — vale para `primary`, `neutral` e `inverted`. */
const SX_COMPACT = { padding: '9px 16px' } satisfies SystemStyleObject<Theme>;
const SX_TALL = { height: '40px', padding: '0 20px' } satisfies SystemStyleObject<Theme>;

/** Tons cuja geometria vem do artboard e que por isso ignoram `size`. */
const TEM_GEOMETRIA_PROPRIA: readonly DsButtonTone[] = ['success', 'error', 'plain'];

/** `variant` do MUI por tom — escolhido pelo casco, nunca repassado pela view (§1.2). */
const VARIANTE_MUI: Record<DsButtonTone, ButtonProps['variant']> = {
  primary: 'contained',
  inverted: 'contained',
  neutral: 'outlined',
  success: 'outlined',
  error: 'outlined',
  plain: 'text',
};

/**
 * `color` do MUI por tom. `inverted` vai de `primary` de propósito: com `inherit` o
 * ramo `inheritColor` do tema venceria o `sx` no dark e o botão ficaria branco —
 * exatamente o defeito que L09 relatou.
 */
const COR_MUI: Record<DsButtonTone, ButtonProps['color']> = {
  primary: 'primary',
  inverted: 'primary',
  neutral: 'inherit',
  success: 'success',
  error: 'error',
  plain: 'inherit',
};

// ----------------------------------------------------------------------

export function DsButton({
  tone = 'primary',
  size = 'compact',
  icon,
  iconSize = 16,
  loading,
  loadingPosition,
  children,
  sx,
  ...other
}: DsButtonProps) {
  const geometriaPorTamanho = !TEM_GEOMETRIA_PROPRIA.includes(tone);

  // Com o indicador no slot de ícone, o `icon` do kit viraria uma segunda marca do mesmo
  // estado. No centro (padrão) o rótulo inteiro fica transparente e o ícone some junto.
  const mostraIcone =
    Boolean(icon) && !(loading && (loadingPosition === 'start' || loadingPosition === 'end'));

  return (
    <Button
      disableElevation
      variant={VARIANTE_MUI[tone]}
      color={COR_MUI[tone]}
      loading={loading}
      loadingPosition={loadingPosition}
      sx={[
        SX_BASE,
        tone === 'primary' && SX_PRIMARY,
        tone === 'neutral' && SX_NEUTRAL,
        tone === 'plain' && SX_PLAIN,
        geometriaPorTamanho && (size === 'tall' ? SX_TALL : SX_COMPACT),
        tone === 'success' && sxSuccess,
        tone === 'success' && sxSuccessDark,
        tone === 'error' && sxError,
        tone === 'inverted' && sxInverted,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {mostraIcone && icon ? <DsIcon name={icon} size={iconSize} /> : null}
      {children}
    </Button>
  );
}
